import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Plus, Search, Filter, ChevronDown, ChevronLeft, ChevronRight, Eye, AlertTriangle, CheckCircle2, AlertCircle, XCircle, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const toTitle = (s) => s ? s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '';

const RIESGO_COLORS = {
  BAJO: 'bg-green-100 text-green-700',
  MEDIO: 'bg-yellow-100 text-yellow-700',
  ALTO: 'bg-orange-100 text-orange-700',
  CRITICO: 'bg-red-100 text-red-700',
};

const SEMAFORO_CONFIG = {
  OK: { color: 'text-[#a05e49]', icon: CheckCircle2, label: 'OK' },
  ALERTA: { color: 'text-[#a05e49]mber-500', icon: AlertTriangle, label: 'ALERTA' },
  CRITICO: { color: 'text-red-600', icon: XCircle, label: 'CRITICO' },
};

const ESTADO_COLORS = {
  EN_VALIDACION: 'bg-blue-50 text-blue-700 border border-blue-200',
  PEND_APROBACION: 'bg-purple-50 text-purple-700 border border-purple-200',
  APROBADA: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  RECHAZADA: 'bg-red-50 text-red-700 border border-red-200',
  CERRADA: 'bg-slate-100 text-slate-700 border border-slate-300',
};

const ESTADO_LABELS = {
  EN_VALIDACION: 'EN VALIDACION',
  PEND_APROBACION: 'PEND. APROBACION',
  APROBADA: 'APROBADA',
  RECHAZADA: 'RECHAZADA',
  CERRADA: 'CERRADA',
};

export default function ActividadesLista() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroMandante, setFiltroMandante] = useState('');
  const [filtroSitio, setFiltroSitio] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [opciones, setOpciones] = useState({ mandantes: [], sitios: [], estados: [] });
  const [page, setPage] = useState(1);
  const PER_PAGE = 6;

  const fetchActividades = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filtroMandante) params.set('mandante', filtroMandante);
      if (filtroSitio) params.set('sitio', filtroSitio);
      if (filtroEstado) params.set('estado', filtroEstado);
      const res = await axios.get(`${API}/actividades-operacionales?${params}`, { headers });
      setActividades(res.data);
      setPage(1);
    } catch { }
    setLoading(false);
  }, [token, search, filtroMandante, filtroSitio, filtroEstado]);

  const fetchOpciones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/actividades-operacionales/opciones`, { headers });
      setOpciones(res.data);
    } catch { }
  }, [token]);

  useEffect(() => { fetchActividades(); }, [fetchActividades]);
  useEffect(() => { fetchOpciones(); }, [fetchOpciones]);

  return (
    <div className="space-y-6" data-testid="actividades-lista">
      <div className="flex items-center justify-end">
        <button onClick={() => navigate('/planificador/actividades/nueva')}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#a05e49] text-white text-sm font-medium rounded-lg hover:bg-[#8b4f3d] transition-colors shadow-sm"
          data-testid="nueva-actividad-btn">
          <Plus className="w-4 h-4" />Nueva Actividad
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar actividades..."
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#a05e49]/20 focus:border-[#c4836e]"
              data-testid="search-actividades" />
          </div>
          <select value={filtroMandante} onChange={e => setFiltroMandante(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none"
            data-testid="filter-mandante">
            <option value="">Todos los mandantes</option>
            {opciones.mandantes?.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={filtroSitio} onChange={e => setFiltroSitio(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none"
            data-testid="filter-sitio">
            <option value="">Todos los sectores</option>
            {opciones.sitios?.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none"
            data-testid="filter-estado">
            <option value="">Todos los estados</option>
            {opciones.estados?.map(e => <option key={e} value={e}>{ESTADO_LABELS[e] || e}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#a05e49]" />
          </div>
        ) : actividades.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <AlertCircle className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-medium">No hay actividades registradas</p>
            <p className="text-xs text-slate-400 mt-1">Cree una nueva actividad para comenzar</p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="actividades-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Orden de Trabajo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Titulo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Empresa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Riesgo</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Semaforo</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Estado</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Avance</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {actividades.slice((page - 1) * PER_PAGE, page * PER_PAGE).map(a => {
                  const sem = SEMAFORO_CONFIG[a.semaforo] || SEMAFORO_CONFIG.OK;
                  const SemIcon = sem.icon;
                  return (
                    <tr key={a.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`actividad-row-${a.id}`}>
                      <td className="px-4 py-3">
                        <span className="text-sm font-mono text-slate-600">{a.codigo}</span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-800">{toTitle(a.titulo)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600">{toTitle(a.empresa_nombre)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600">{a.fecha_planificada.split('-').reverse().join('/')}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded ${RIESGO_COLORS[a.nivel_riesgo] || RIESGO_COLORS.BAJO}`}>
                          {a.nivel_riesgo}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${sem.color}`}>
                          <SemIcon className="w-3.5 h-3.5" />{sem.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block text-[11px] font-medium px-2.5 py-1 rounded ${ESTADO_COLORS[a.estado] || ''}`}>
                          {ESTADO_LABELS[a.estado] || a.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 min-w-[80px]">
                          <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${a.porcentaje_avance || 0}%`, backgroundColor: a.estado === 'CERRADA' ? '#059669' : '#00827F' }} />
                          </div>
                          <span className="text-[10px] font-bold text-slate-600 w-8 text-right">{a.porcentaje_avance || 0}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => navigate(`/planificador/actividades/${a.id}`)}
                            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                            title="Ver detalle"
                            data-testid={`ver-actividad-${a.id}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={async () => {
                              try {
                                const res = await axios.get(`${API}/actividades-operacionales/${a.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
                                const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                window.open(url, '_blank');
                              } catch { alert('Error al generar PDF'); }
                            }}
                            className="p-1.5 hover:bg-[#00827F]/10 rounded-lg text-[#00827F] transition-colors"
                            title="Descargar PDF"
                            data-testid={`pdf-actividad-${a.id}`}>
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {Math.ceil(actividades.length / PER_PAGE) > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200" data-testid="pagination-actividades">
              <span className="text-xs text-slate-400">{(page - 1) * PER_PAGE + 1}-{Math.min(page * PER_PAGE, actividades.length)} de {actividades.length} actividades</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed">
                  <ChevronLeft className="w-3.5 h-3.5" /> Anterior
                </button>
                {Array.from({ length: Math.ceil(actividades.length / PER_PAGE) }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`w-8 h-8 text-xs rounded-lg transition-colors ${p === page ? 'bg-[#a05e49] text-white' : 'border border-slate-200 hover:bg-slate-50 text-slate-600'}`}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setPage(p => Math.min(Math.ceil(actividades.length / PER_PAGE), p + 1))} disabled={page === Math.ceil(actividades.length / PER_PAGE)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed">
                  Siguiente <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}
